/**
 * Unidades y conversión (Req. 5; design.md, sección 5).
 *
 * Se guarda siempre en kg y cm con precisión completa, junto con lo que el
 * usuario escribió. La conversión ocurre solo al capturar y al presentar; los
 * cálculos usan el valor canónico.
 */

import { ErrorConversionUnidades } from './errors';
import { formatearNumero } from './numbers';

/** Factores exactos por definición internacional (Req. 5.5). */
export const KG_POR_LB = 0.45359237;
export const CM_POR_IN = 2.54;

export type UnidadPeso = 'kg' | 'lb';
export type UnidadLongitud = 'cm' | 'in';
export type UnidadCaptura = UnidadPeso | UnidadLongitud;

export const UNIDADES_PESO: readonly UnidadPeso[] = ['kg', 'lb'];
export const UNIDADES_LONGITUD: readonly UnidadLongitud[] = ['cm', 'in'];

/** Lo que el usuario escribió, tal cual (Req. 5.4, 5.8). */
export interface Capturado {
  valor: number;
  unidad: UnidadCaptura;
}

/**
 * Cómo se presenta un peso (Req. 5.6):
 * - corporal: siempre 1 decimal ("70.0 kg")
 * - ejercicio: hasta 2 decimales sin ceros sobrantes ("22.5 kg", "40 kg", "1.25 kg")
 */
export type UsoPeso = 'corporal' | 'ejercicio';

export function esUnidadPeso(valor: unknown): valor is UnidadPeso {
  return valor === 'kg' || valor === 'lb';
}

export function esUnidadLongitud(valor: unknown): valor is UnidadLongitud {
  return valor === 'cm' || valor === 'in';
}

function exigirUnidadPeso(unidad: unknown): UnidadPeso {
  if (!esUnidadPeso(unidad)) {
    throw new ErrorConversionUnidades(`Unidad de peso desconocida: "${String(unidad)}".`);
  }
  return unidad;
}

function exigirUnidadLongitud(unidad: unknown): UnidadLongitud {
  if (!esUnidadLongitud(unidad)) {
    throw new ErrorConversionUnidades(`Unidad de longitud desconocida: "${String(unidad)}".`);
  }
  return unidad;
}

// ---------------------------------------------------------------------------
// Conversión
// ---------------------------------------------------------------------------

export function aKg(valor: number, unidad: UnidadPeso): number {
  return exigirUnidadPeso(unidad) === 'kg' ? valor : valor * KG_POR_LB;
}

export function desdeKg(kg: number, unidad: UnidadPeso): number {
  return exigirUnidadPeso(unidad) === 'kg' ? kg : kg / KG_POR_LB;
}

export function aCm(valor: number, unidad: UnidadLongitud): number {
  return exigirUnidadLongitud(unidad) === 'cm' ? valor : valor * CM_POR_IN;
}

export function desdeCm(cm: number, unidad: UnidadLongitud): number {
  return exigirUnidadLongitud(unidad) === 'cm' ? cm : cm / CM_POR_IN;
}

export function otraUnidadPeso(unidad: UnidadPeso): UnidadPeso {
  return exigirUnidadPeso(unidad) === 'kg' ? 'lb' : 'kg';
}

export function otraUnidadLongitud(unidad: UnidadLongitud): UnidadLongitud {
  return exigirUnidadLongitud(unidad) === 'cm' ? 'in' : 'cm';
}

// ---------------------------------------------------------------------------
// Cargas del gym (Req. 9.5): kg, lb o el número de placa de una máquina
// ---------------------------------------------------------------------------

/**
 * Unidad de la carga de un ejercicio. "placa" es el número que marca la
 * máquina (por ejemplo, 7 placas del aparato): no se convierte, porque cada
 * máquina es distinta y no se equiparan cargas entre ellas.
 */
export type UnidadCarga = UnidadPeso | 'placa';

export const UNIDADES_CARGA: readonly UnidadCarga[] = ['lb', 'kg', 'placa'];

export function esUnidadCarga(valor: unknown): valor is UnidadCarga {
  return valor === 'kg' || valor === 'lb' || valor === 'placa';
}

/** La carga tal como se escribió en una serie. */
export interface CargaCapturada {
  valor: number;
  unidad: UnidadCarga;
}

/** Lo que se guarda de una carga: en kg si se puede convertir (null en placas) y lo capturado. */
export function capturarCarga(
  valor: number,
  unidad: UnidadCarga,
): { pesoKg: number | null; capturado: CargaCapturada } {
  return { pesoKg: unidad === 'placa' ? null : aKg(valor, unidad), capturado: { valor, unidad } };
}

/** "22.5 lb", "40 kg", "1 placa", "7 placas". */
export function formatearCarga(carga: CargaCapturada): string {
  const texto = formatearNumero(carga.valor, { decimales: 2, fijos: false });
  if (carga.unidad === 'placa') return carga.valor === 1 ? '1 placa' : `${texto} placas`;
  return `${texto} ${carga.unidad}`;
}

export const ETIQUETAS_UNIDAD_CARGA: Readonly<Record<UnidadCarga, string>> = {
  lb: 'lb',
  kg: 'kg',
  placa: 'Placa',
};

/** Arma lo que se guarda de un peso: el canónico en kg y lo capturado. */
export function capturarPeso(
  valor: number,
  unidad: UnidadPeso,
): { pesoKg: number; capturado: Capturado } {
  return { pesoKg: aKg(valor, unidad), capturado: { valor, unidad } };
}

/** Arma lo que se guarda de una longitud: el canónico en cm y lo capturado. */
export function capturarLongitud(
  valor: number,
  unidad: UnidadLongitud,
): { valorCm: number; capturado: Capturado } {
  return { valorCm: aCm(valor, unidad), capturado: { valor, unidad } };
}

// ---------------------------------------------------------------------------
// Presentación
// ---------------------------------------------------------------------------

export function formatearPeso(kg: number, unidad: UnidadPeso, uso: UsoPeso = 'corporal'): string {
  const valor = desdeKg(kg, unidad);
  const texto =
    uso === 'corporal'
      ? formatearNumero(valor, { decimales: 1 })
      : formatearNumero(valor, { decimales: 2, fijos: false });
  return `${texto} ${unidad}`;
}

export function formatearLongitud(cm: number, unidad: UnidadLongitud): string {
  return `${formatearNumero(desdeCm(cm, unidad), { decimales: 1 })} ${unidad}`;
}

/** Lo capturado tal como se escribió, para el detalle: "154.3 lb" (Req. 5.8). */
export function formatearCapturado(capturado: Capturado): string {
  return `${formatearNumero(capturado.valor, { decimales: 2, fijos: false })} ${capturado.unidad}`;
}

/**
 * Equivalencia en vivo mientras se escribe (Req. 5.3): "= 70.0 kg".
 * Devuelve null si las dos unidades son la misma: no hay nada que convertir.
 */
export function equivalenciaPeso(
  valor: number,
  de: UnidadPeso,
  a: UnidadPeso,
  uso: UsoPeso = 'corporal',
): string | null {
  if (de === a) return null;
  return `= ${formatearPeso(aKg(valor, de), a, uso)}`;
}

export function equivalenciaLongitud(
  valor: number,
  de: UnidadLongitud,
  a: UnidadLongitud,
): string | null {
  if (de === a) return null;
  return `= ${formatearLongitud(aCm(valor, de), a)}`;
}
